/* eslint react-hooks/exhaustive-deps: "error" */
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import GET_INBOX from 'Jsx/CrehanaLive/graphql/GetLiveInbox.graphql';
import GET_INBOX_B2B from 'Jsx/CrehanaLive/graphql/GetLiveInboxB2B.graphql';

import type {
  GetLiveInbox,
  GetLiveInboxVariables,
  GetLiveInbox_live_inbox as Inbox,
  GetLiveInbox_live_inbox_chatSet_edges as Edges,
} from '../graphql/types/GetLiveInbox';

import {
  GetLiveInboxB2B,
  GetLiveInboxB2BVariables,
} from '../graphql/types/GetLiveInboxB2B';
import { ChatNode } from '../graphql/types/ChatNode';

import matchMediaQuery from '../../Utils/matchMediaQuery';

import useRestoreFromBackground from './useRestoreFromBackground';

interface Variables {
  slug: string;
  orgSlug?: string;
}

type UseLiveQueryInboxOptions = {
  isB2B: boolean;
};

function mergeInbox(
  prevData: GetLiveInbox,
  data: Partial<Inbox>,
): GetLiveInbox {
  return {
    ...prevData,
    live: {
      ...prevData.live!,
      inbox: {
        ...prevData.live!.inbox!,
        ...data,
      },
      __typename: prevData.live!.__typename,
    },
  };
}

function getChatNode(data: ChatNode): ChatNode {
  return {
    ...data,
    id: data.originalId!.toString(),
    originalId: data.originalId!.toString(),
    user: {
      ...data.user!,
      __typename: 'UserNode',
    },
    __typename: 'ChatNode',
  };
}

const removeById = (data: ChatNode) => (edge: Edges | null) =>
  edge?.node?.originalId !== data.originalId?.toString();

function useLiveQueryInbox(
  { slug, orgSlug }: Variables,
  options?: UseLiveQueryInboxOptions,
) {
  const isB2B = !!options?.isB2B;
  const response = useQuery<GetLiveInbox, GetLiveInboxVariables>(GET_INBOX, {
    variables: {
      slug,
    },
    skip: isB2B,
    fetchPolicy: 'cache-first',
  });
  const responseB2B = useQuery<GetLiveInboxB2B, GetLiveInboxB2BVariables>(
    GET_INBOX_B2B,
    {
      variables: {
        slug,
        orgSlug: isB2B ? orgSlug! : '',
      },
      skip: !isB2B,
      fetchPolicy: 'cache-first',
    },
  );

  const { loading, data, error, refetch, updateQuery } = isB2B
    ? responseB2B
    : response;

  useRestoreFromBackground(() => {
    const isDesktop = matchMediaQuery('md');

    // Only fire reftch on mobile
    if (!isDesktop.matches) {
      refetch();
    }
  });

  const questions: ChatNode[] = useMemo(() => {
    if (data?.live?.inbox?.chatQuestion?.edges.length) {
      return data.live.inbox.chatQuestion.edges.map(e => e!.node!);
    }
    return [];
  }, [data]);

  const messages: ChatNode[] = useMemo(() => {
    if (data?.live?.inbox?.chatSet?.edges?.length) {
      return data.live.inbox.chatSet.edges.map(edge => edge!.node!);
    }
    return [];
  }, [data]);

  const chatPinned: ChatNode | undefined = useMemo(() => {
    if (data?.live?.inbox?.chatPinned) {
      return data.live.inbox.chatPinned;
    }
  }, [data]);

  const onReceiveMessage = (chatNode: ChatNode) => {
    updateQuery(prevData =>
      mergeInbox(prevData, {
        chatSet: {
          ...prevData.live!.inbox!.chatSet!,
          edges: [
            ...prevData.live!.inbox!.chatSet!.edges!,
            {
              __typename: 'ChatNodeEdge',
              node: getChatNode(chatNode),
            },
          ],
        },
      }),
    );
  };

  const onPinMessage = (chatNode: ChatNode) => {
    updateQuery(prevData =>
      mergeInbox(prevData, {
        chatPinned: getChatNode(chatNode),
        chatSet: {
          ...prevData.live!.inbox!.chatSet!,
          edges: prevData.live!.inbox!.chatSet!.edges.filter(
            removeById(chatNode),
          ),
        },
      }),
    );
  };

  const onUnpinMessage = () => {
    updateQuery(prevData => mergeInbox(prevData, { chatPinned: null }));
  };

  const onHideMessage = (chatNode: ChatNode) => {
    updateQuery(prevData =>
      mergeInbox(prevData, {
        chatSet: {
          ...prevData.live!.inbox!.chatSet!,
          edges: prevData.live!.inbox!.chatSet!.edges.filter(
            removeById(chatNode),
          ),
        },
      }),
    );
  };

  const onMoveQuestion = (chatNode: ChatNode) => {
    updateQuery(prevData => {
      let edges: (Edges | null)[] = [];

      if (chatNode.isQuestion) {
        edges = [
          ...prevData.live!.inbox!.chatQuestion!.edges,
          {
            __typename: 'ChatNodeEdge',
            node: getChatNode(chatNode),
          },
        ];
      } else {
        edges = [
          ...prevData.live!.inbox!.chatQuestion!.edges.filter(
            removeById(chatNode),
          ),
        ];
      }

      return mergeInbox(prevData, {
        chatQuestion: {
          ...prevData.live!.inbox!.chatQuestion!,
          edges,
        },
      });
    });
  };

  const onLikeMessage = (chatNode: ChatNode) => {
    updateQuery(prevData =>
      mergeInbox(prevData, {
        chatQuestion: {
          ...prevData.live!.inbox!.chatQuestion!,
          edges: prevData
            .live!.inbox!.chatQuestion!.edges.map(edge =>
              edge?.node?.originalId === chatNode.originalId?.toString()
                ? {
                    ...edge!,
                    node: getChatNode(chatNode),
                  }
                : edge,
            )
            .sort((a, b) => (a!.node!.likes > b!.node!.likes ? -1 : 1)),
        },
      }),
    );
  };

  return {
    error,
    loading,
    refetch,
    messages,
    chatPinned,
    questions,
    onReceiveMessage,
    onPinMessage,
    onUnpinMessage,
    onHideMessage,
    onMoveQuestion,
    onLikeMessage,
  };
}

export default useLiveQueryInbox;
